import type { AdminFundUpdateEntry, CmsBlock } from '../../models/FundUpdateEntry';
import type { HighlightEditState } from './fundUpdatesAdmin.types';

const MONTH_SHORT = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC',
];

/**
 * Today's date as YYYY-MM-DD in the admin's own local timezone. Deliberately
 * not `new Date().toISOString().slice(0, 10)` — that converts to UTC first,
 * which rolls back to "yesterday" for anyone in a positive UTC offset (e.g.
 * Australia) during their early-morning local hours.
 */
export function todayDateString(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function formatFundUpdateDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

export function formatHighlightDate(dateStr: string): string {
  if (!dateStr) return '';
  const parts = dateStr.split('T')[0].split('-');
  const year = (parts[0] ?? '').slice(-2);
  const month = parseInt(parts[1] ?? '1', 10);
  const day = parseInt(parts[2] ?? '1', 10);
  return `${day} ${MONTH_SHORT[Math.max(0, month - 1)] ?? ''} ${year}`;
}

/** Extract all image URLs referenced in a content block array */
export function extractImageUrlsFromContent(content: CmsBlock[]): string[] {
  const urls: string[] = [];
  for (const block of content) {
    const props = block.properties as Record<string, unknown>;
    if (block.type === 'ImageCarousel' && Array.isArray(props.images)) {
      for (const img of props.images as Array<{ imageUrl?: string | { url?: string } }>) {
        const raw = img?.imageUrl;
        const url = typeof raw === 'string' ? raw : (raw?.url ?? null);
        if (url && !urls.includes(url)) urls.push(url);
      }
    } else if (block.type === 'HeroImage') {
      const url = (props.imageUrl as { url?: string } | undefined)?.url;
      if (url && !urls.includes(url)) urls.push(url);
    } else if (
      block.type === 'LeftRightList' &&
      typeof props.imageUrl === 'string' &&
      !urls.includes(props.imageUrl)
    ) {
      urls.push(props.imageUrl);
    } else if (
      block.type === 'StandardHeaderText' &&
      typeof props.imageUrl === 'string' &&
      !urls.includes(props.imageUrl)
    ) {
      urls.push(props.imageUrl);
    }
  }
  return urls;
}

function headingText(heading: unknown): string {
  if (typeof heading === 'string') return heading;
  if (heading && typeof heading === 'object' && 'content' in heading) {
    return String((heading as { content?: string }).content || '');
  }
  return '';
}

function contentText(content: unknown): string {
  if (typeof content === 'string') return content;
  if (content && typeof content === 'object' && 'content' in content) {
    return String((content as { content?: string }).content || '');
  }
  return '';
}

/** Infer a highlight card from content blocks (mirrors native app logic) */
export function inferHighlightFromContent(content: CmsBlock[]): {
  title: string;
  summary: string;
  imageUrl: string | null;
} {
  for (const block of content) {
    const props = block.properties as Record<string, unknown>;
    if (block.type === 'StandardHeaderText') {
      const headingStr = headingText(props.heading);
      console.log('[inferHighlightFromContent] heading object:', JSON.stringify(props.heading));
      const rawContent = contentText(props.content);
      const summary = rawContent
        .replace(/<[^>]+>/g, '')
        .trim()
        .slice(0, 200);
      return { title: headingStr, summary, imageUrl: null };
    }
    if (block.type === 'ImageCarousel') {
      const headingStr = headingText(props.heading);
      const firstItem = ((props.images as unknown[]) || [])[0] as
        | { text?: { content?: string }; imageUrl?: string | { url?: string } }
        | undefined;
      const summary = firstItem?.text?.content
        ? firstItem.text.content.replace(/<[^>]+>/g, '').trim()
        : '';
      const imgUrl = firstItem?.imageUrl;
      const imageUrl = typeof imgUrl === 'string' ? imgUrl : (imgUrl?.url ?? null);
      return { title: headingStr, summary, imageUrl };
    }
  }
  return { title: '', summary: '', imageUrl: null };
}

export function resolveHighlightEditState(
  update: AdminFundUpdateEntry,
  highlightEdits: Record<string, HighlightEditState>,
): HighlightEditState {
  const id = update.externalId;
  if (highlightEdits[id]) return highlightEdits[id];

  const hasCard = !!update.updateCard;
  const card = update.updateCard;
  const inferred = inferHighlightFromContent(update.content ?? []);
  const previewTitle = hasCard ? card!.properties.title : inferred.title;
  const previewSummary = hasCard ? card!.properties.summary : inferred.summary;
  const previewImage = hasCard ? (card!.properties.imageUrl ?? null) : inferred.imageUrl;

  return {
    title: previewTitle,
    summary: previewSummary,
    imageUrl: previewImage ?? '',
  };
}

/** The id of the update with the latest `updateDate` (the first listed wins a tie), or null. */
export function mostRecentUpdateId(
  updates: Array<{ externalId: string; updateDate: string }>,
): string | null {
  let newest: { externalId: string; updateDate: string } | null = null;
  for (const update of updates) {
    if (!newest || update.updateDate > newest.updateDate) newest = update;
  }
  return newest?.externalId ?? null;
}
